
import React, { useState, useEffect } from 'react';
import { Calendar, Clock, User, Phone, FileText, MapPin, Stethoscope } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { TIME_CONSTANTS } from '../lib/constants';
import { ScheduleItem, Appointment, Doctor, Staff, Room } from './SchedulerTypes';

interface AppointmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (appointmentData: Omit<Appointment, 'id' | 'createdAt' | 'updatedAt'>) => void;
  scheduleItem: ScheduleItem | null;
  existingAppointment: Appointment | null;
  doctors: Doctor[];
  staff: Staff[];
  rooms: Room[];
}

const AppointmentModal: React.FC<AppointmentModalProps> = ({
  isOpen,
  onClose,
  onSave,
  scheduleItem,
  existingAppointment,
  doctors,
  staff,
  rooms,
}) => {
  const { toast } = useToast();
  const [patientName, setPatientName] = useState('');
  const [patientContact, setPatientContact] = useState('');
  const [doctorId, setDoctorId] = useState('');
  const [roomId, setRoomId] = useState<string | undefined>('');
  const [procedure, setProcedure] = useState('');
  const [status, setStatus] = useState<'scheduled' | 'completed' | 'cancelled' | 'no-show'>('scheduled');
  const [notes, setNotes] = useState('');
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [duration, setDuration] = useState<number>(TIME_CONSTANTS.DEFAULT_APPOINTMENT_DURATION);

  useEffect(() => {
    if (scheduleItem) {
      setStartTime(scheduleItem.startTime);
      setEndTime(scheduleItem.endTime);
    }
    if (existingAppointment) {
      setPatientName(existingAppointment.patientName);
      setPatientContact(existingAppointment.patientContact);
      setDoctorId(existingAppointment.doctorId);
      setRoomId(existingAppointment.roomId || '');
      setProcedure(existingAppointment.procedure);
      setStatus(existingAppointment.status);
      setNotes(existingAppointment.notes || '');
      setDuration(existingAppointment.duration);
    } else {
      setPatientName('');
      setPatientContact('');
      setDoctorId('');
      setRoomId('');
      setProcedure('');
      setStatus('scheduled');
      setNotes('');
      setDuration(TIME_CONSTANTS.DEFAULT_APPOINTMENT_DURATION);
    }
  }, [scheduleItem, existingAppointment]);

  const handleSubmit = () => {
    if (!patientName || !patientContact || !doctorId || !procedure) {
      toast({
        title: "Validation Error",
        description: "Please fill in all required fields.",
        variant: "destructive",
      });
      return;
    }

    if (!scheduleItem) {
      console.error('Schedule item is null.');
      return;
    }

    const appointmentData: Omit<Appointment, 'id' | 'createdAt' | 'updatedAt'> = {
      scheduleItemId: scheduleItem.id,
      date: scheduleItem.date,
      startTime: startTime,
      endTime: endTime,
      patientName: patientName,
      patientContact: patientContact,
      doctorId: doctorId,
      roomId: roomId,
      procedure: procedure,
      status: status,
      notes: notes,
      duration: duration,
      clinic_id: scheduleItem.clinic_id,
    };
    onSave(appointmentData);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[525px]">
        <DialogHeader>
          <DialogTitle>
            {existingAppointment ? 'Edit Appointment' : 'Book Appointment'}
          </DialogTitle>
        </DialogHeader>

        <div className="grid gap-4 py-4">
          <div className="grid grid-cols-1 items-start gap-2 sm:grid-cols-4 sm:items-center sm:gap-4">
            <Label htmlFor="patientName" className="text-left sm:text-right">
              <User className="inline-block h-4 w-4 mr-2" />
              Patient Name
            </Label>
            <Input id="patientName" value={patientName} onChange={(e) => setPatientName(e.target.value)} className="w-full sm:col-span-3" />
          </div>

          <div className="grid grid-cols-1 items-start gap-2 sm:grid-cols-4 sm:items-center sm:gap-4">
            <Label htmlFor="patientContact" className="text-left sm:text-right">
              <Phone className="inline-block h-4 w-4 mr-2" />
              Contact Number
            </Label>
            <Input id="patientContact" value={patientContact} onChange={(e) => setPatientContact(e.target.value)} className="w-full sm:col-span-3" />
          </div>

          <div className="grid grid-cols-1 items-start gap-2 sm:grid-cols-4 sm:items-center sm:gap-4">
            <Label htmlFor="doctor" className="text-left sm:text-right">
              <Stethoscope className="inline-block h-4 w-4 mr-2" />
              Doctor
            </Label>
            <Select value={doctorId} onValueChange={setDoctorId}>
              <SelectTrigger className="w-full sm:col-span-3">
                <SelectValue placeholder="Select a doctor" />
              </SelectTrigger>
              <SelectContent>
                {doctors?.filter(doctor => doctor.status === 'active').map((doctor) => (
                  <SelectItem key={doctor.id} value={doctor.id}>{doctor.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-1 items-start gap-2 sm:grid-cols-4 sm:items-center sm:gap-4">
            <Label htmlFor="room" className="text-left sm:text-right">
              <MapPin className="inline-block h-4 w-4 mr-2" />
              Room (Optional)
            </Label>
            <Select value={roomId || 'none'} onValueChange={(value) => setRoomId(value === 'none' ? undefined : value)}>
              <SelectTrigger className="w-full sm:col-span-3">
                <SelectValue placeholder="Select a room" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">None</SelectItem>
                {rooms?.filter(room => room.status === 'active').map((room) => (
                  <SelectItem key={room.id} value={room.id}>{room.name} ({room.clinic})</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-1 items-start gap-2 sm:grid-cols-4 sm:items-center sm:gap-4">
            <Label htmlFor="procedure" className="text-left sm:text-right">
              <FileText className="inline-block h-4 w-4 mr-2" />
              Procedure
            </Label>
            <Input id="procedure" value={procedure} onChange={(e) => setProcedure(e.target.value)} className="w-full sm:col-span-3" />
          </div>

          <div className="grid grid-cols-1 items-start gap-2 sm:grid-cols-4 sm:items-center sm:gap-4">
            <Label htmlFor="status" className="text-left sm:text-right">
              <Calendar className="inline-block h-4 w-4 mr-2" />
              Status
            </Label>
            <Select value={status} onValueChange={(value: 'scheduled' | 'completed' | 'cancelled' | 'no-show') => setStatus(value)}>
              <SelectTrigger className="w-full sm:col-span-3">
                <SelectValue placeholder="Select status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="scheduled">Scheduled</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
                <SelectItem value="no-show">No-show</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-1 items-start gap-2 sm:grid-cols-4 sm:items-center sm:gap-4">
            <Label htmlFor="notes" className="text-left sm:text-right">
              <FileText className="inline-block h-4 w-4 mr-2" />
              Notes
            </Label>
            <Textarea id="notes" value={notes} onChange={(e) => setNotes(e.target.value)} className="w-full sm:col-span-3" />
          </div>

          <div className="grid grid-cols-1 items-start gap-2 sm:grid-cols-4 sm:items-center sm:gap-4">
            <Label htmlFor="duration" className="text-left sm:text-right">
              <Clock className="inline-block h-4 w-4 mr-2" />
              Duration (mins)
            </Label>
            <Input
              type="number"
              id="duration"
              value={duration}
              onChange={(e) => setDuration(parseInt(e.target.value) || 0)}
              className="w-full sm:col-span-3"
            />
          </div>
        </div>

        <DialogFooter>
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" onClick={handleSubmit}>
            {existingAppointment ? 'Update Appointment' : 'Book Appointment'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AppointmentModal;
